import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, RefreshCw, Search, Pencil, Trash2,
  ChevronUp, ChevronDown, Link2, CheckCircle2, XCircle, Loader2,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import IntegrationFormModal from '../../components/admin/IntegrationFormModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast from '../../components/admin/Toast';
import { useIsMobile } from '../../hooks/useIsMobile';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';
import '../../components/admin/MobileCardList.css';

const IntegrationsManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<any | null>(null);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false,
    message: '',
    type: 'success'
  });

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getIntegrations();
      const data = response.data || response;
      const integrationsList = Array.isArray(data) ? data : (data.results || []);
      setIntegrations(integrationsList);
    } catch (error) {
      console.error('Failed to load integrations:', error);
      setToast({ show: true, message: 'Failed to load integrations', type: 'error' });
      setIntegrations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setSelectedIntegration(null);
    setIsModalOpen(true);
  };

  const handleEdit = (integration: any) => {
    setSelectedIntegration(integration);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (integration: any) => {
    setSelectedIntegration(integration);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedIntegration) return;
    
    try {
      await adminAPI.deleteIntegration(selectedIntegration.id);
      setToast({ show: true, message: 'Integration deleted successfully', type: 'success' });
      loadIntegrations();
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ show: true, message: 'Failed to delete integration', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedIntegration(null);
    }
  };

  const handleSave = async (formData: any) => {
    try {
      if (selectedIntegration) {
        await adminAPI.updateIntegration(selectedIntegration.id, formData);
        setToast({ show: true, message: 'Integration updated successfully', type: 'success' });
      } else {
        await adminAPI.createIntegration(formData);
        setToast({ show: true, message: 'Integration created successfully', type: 'success' });
      }
      setIsModalOpen(false);
      setSelectedIntegration(null);
      loadIntegrations();
    } catch (error: any) {
      console.error('Save error:', error);
      throw error; // Re-throw to let form handle validation errors
    }
  };

  const handleSort = (f: string) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
    setCurrentPage(1);
  };
  const SA = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={10} className="adm-sort-arrow" /> : <ChevronDown size={10} className="adm-sort-arrow" />;
  };

  const integrationsArray = Array.isArray(integrations) ? integrations : [];
  const filtered = useMemo(() => {
    let list = integrationsArray;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(i =>
        i.display_name?.toLowerCase().includes(q) ||
        i.service_name?.toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a: any, b: any) => {
      const av = a[sortField] ?? ''; const bv = b[sortField] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return list;
  }, [integrationsArray, searchTerm, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageNums = (() => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); return pages; }
    pages.push(1); if (currentPage > 3) pages.push('...');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('...'); pages.push(totalPages); return pages;
  })();
  const fmtDate = (v: string) => v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const envColor = (env: string) => env === 'production' ? 'adm-chip-green' : env === 'sandbox' ? 'adm-chip-amber' : 'adm-chip-gray';

  return (
    <div className="admin-page adm-page">
      <div className="adm-topbar">
        <h1 className="adm-page-title">Integrations</h1>
        <div className="adm-topbar-right">
          <button className="adm-btn adm-btn-add" onClick={handleAddNew}>
            <Plus size={13} /> Add Integration <span className="adm-btn-badge">{integrations.length}</span>
          </button>
          <button className="adm-btn adm-btn-refresh" onClick={loadIntegrations} disabled={loading}>
            {loading ? <span className="adm-spin"><Loader2 size={13} /></span> : <RefreshCw size={13} />} Refresh
          </button>
        </div>
      </div>
      <div className="adm-filter-wrap">
        <div className="adm-filter-left">
          <span className="adm-count"><strong>{filtered.length}</strong> integrations</span>
        </div>
        <div className="adm-filter-right">
          <div className="adm-search-wrap">
            <span className="adm-search-icon"><Search size={13} /></span>
            <input className="adm-search-input" placeholder="Search by name…" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>

      {/* Mobile cards */}
      {isMobile && (
        <div className="mobile-card-list">
          {loading && <div className="mc-loading">Loading integrations…</div>}
          {!loading && filtered.length === 0 && <div className="mc-empty">No integrations found.</div>}
          {!loading && filtered.map((ig: any) => (
            <div className="mobile-card" key={ig.id}>
              <div className="mc-top">
                <div className="mc-thumb-placeholder"><Link2 size={22} /></div>
                <div className="mc-info">
                  <div className="mc-title">{ig.display_name}</div>
                  <div className="mc-subtitle">{ig.integration_type_display}</div>
                </div>
                <div className="mc-badge-corner">
                  <span className={`mc-active-pill ${ig.is_active ? 'active' : 'inactive'}`}>{ig.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
              <div className="mc-meta">
                <div className="mc-meta-item"><span className="mc-meta-label">Env:</span><span className="mc-meta-value">{ig.environment_display}</span></div>
                <div className="mc-meta-item"><span className="mc-meta-label">Key:</span><span className="mc-meta-value">{ig.api_key_masked}</span></div>
              </div>
              <div className="mc-actions">
                <button className="mc-btn mc-btn-edit" onClick={() => handleEdit(ig)}><Pencil size={12} /> Edit</button>
                <button className="mc-btn mc-btn-delete" onClick={() => handleDeleteClick(ig)}><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Desktop table */}
      {!isMobile && (
        <div className="adm-table-card">
          <div className="adm-table-scroll">
            <table className="adm-table">
              <thead>
                <tr>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('display_name')}>Name <SA field="display_name" /></th>
                  <th className="adm-th">Type</th>
                  <th className="adm-th">Environment</th>
                  <th className="adm-th">API Key</th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('is_active')}>Status <SA field="is_active" /></th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('updated_at')}>Updated <SA field="updated_at" /></th>
                  <th className="adm-th" style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="adm-loading"><span className="adm-spin" style={{ marginRight: 8 }}><Loader2 size={18} /></span>Loading…</td></tr>}
                {!loading && paginated.length === 0 && <tr><td colSpan={7} className="adm-empty">No integrations found.</td></tr>}
                {!loading && paginated.map((ig: any) => (
                  <tr className="adm-row" key={ig.id}>
                    <td className="adm-td"><span className="adm-name-main">{ig.display_name}</span></td>
                    <td className="adm-td"><span className="adm-chip adm-chip-blue">{ig.integration_type_display}</span></td>
                    <td className="adm-td"><span className={`adm-chip ${envColor(ig.environment)}`}>{ig.environment_display}</span></td>
                    <td className="adm-td"><span className="adm-mono">{ig.api_key_masked || '—'}</span></td>
                    <td className="adm-td">
                      <span className={`adm-chip ${ig.is_active ? 'adm-chip-green' : 'adm-chip-red'}`}>
                        {ig.is_active ? <><CheckCircle2 size={9} /> Active</> : <><XCircle size={9} /> Inactive</>}
                      </span>
                    </td>
                    <td className="adm-td"><span className="adm-date">{fmtDate(ig.updated_at)}</span></td>
                    <td className="adm-td" style={{ padding: '6px 4px' }}>
                      <div className="adm-actions-row">
                        <button className="adm-action-btn adm-ab-edit" onClick={() => handleEdit(ig)} title="Edit"><Pencil size={12} /></button>
                        <button className="adm-action-btn adm-ab-del" onClick={() => handleDeleteClick(ig)} title="Delete"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="adm-pagination">
            <div className="adm-page-size-wrap"><span>Show</span><select className="adm-page-size-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>{[10,20,50].map(n => <option key={n} value={n}>{n}</option>)}</select><span>per page</span></div>
            <div className="adm-page-info">{filtered.length === 0 ? '0 integrations' : `${(currentPage-1)*pageSize+1}–${Math.min(currentPage*pageSize,filtered.length)} of ${filtered.length}`}</div>
            <div className="adm-page-controls">
              <button className="adm-page-btn" disabled={currentPage===1} onClick={() => setCurrentPage(p => p-1)}>‹</button>
              <div className="adm-page-numbers">{pageNums.map((n,i) => n==='...' ? <span key={`e${i}`} style={{padding:'0 4px',color:'#94a3b8'}}>…</span> : <button key={n} className={`adm-page-num${currentPage===n?' active':''}`} onClick={() => setCurrentPage(n as number)}>{n}</button>)}</div>
              <button className="adm-page-btn" disabled={currentPage===totalPages} onClick={() => setCurrentPage(p => p+1)}>›</button>
            </div>
          </div>
        </div>
      )}

      <IntegrationFormModal
        isOpen={isModalOpen}
        onClose={() => { 
          setIsModalOpen(false); 
          setSelectedIntegration(null); 
        }}
        onSave={handleSave}
        integration={selectedIntegration}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedIntegration(null);
        }}
        onConfirm={handleDelete}
        title="Delete Integration"
        message={`Are you sure you want to delete "${selectedIntegration?.display_name || selectedIntegration?.service_name}"? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
      />

      {toast.show && (
        <Toast
          {...toast}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default IntegrationsManagement;
